import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons/faCircleInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { MprcIconButtonDropdown, MprcText } from "components";
import { MprcFilterBox, MprcFilterCountInfo, MprcFilterIconButton, MprcFilterLine } from "components/filter";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { MprcFilterNoRecords } from "components/filter/MprcFilterNoRecords";
import { IconAdd, IconSort } from "components/icons";
import { MprcDropdown } from "components/MprcDropdown";
import { MprcSearchTextField } from "components/MprcSearchTextField";
import { MprcTooltip } from "components/MprcTooltip";
import { CoreFilterCommand, FeedChannelHeaderResponse, FeedType, KnowledgeFeedOrder, KnowledgeQuickFilterType, PublicationType, QuickFilterDto, StickerAppliesTo } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AdvancedFilter, IAdvFilterCommand, IAdvFilterDropdown, IAdvFilterMenuItem } from "views/general/AdvancedFilter";
import { changeQuickFilter, clearAdvFilter, getClearFilter } from "views/knowledge/feed/components/FeedFilter/FeedFilterCommon";
import { publicationFactory } from "views/knowledge/feed/FeedHelpers";
import { containsMenuOptions, IFeedFilterOptions, orderMenuOptions, othersChannelMenuOptions, periodMenuOptions, sourceChannelMenuOptions, stickersMenuOptions } from "../components/FeedFilter";
import { QuickFilter } from "../components/FeedFilter/QuickFilter";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    count?: number;
    filter: IFeedFilterOptions;
    expandedAdvFilter: boolean;
    channel: FeedChannelHeaderResponse;
    onFilterChange: (filter: IFeedFilterOptions) => void;
    onGetQuickFilterOptions: () => Promise<QuickFilterDto[]>;
    onSelectedExpandedAdvFilter: (selected: boolean) => void;
    onError: (error: any) => void;
}

/**
 * Filtro para o feed de Canal
 * @param props
 */
export const FeedChannelFilter: React.FC<IProps> = (props) => {

    const { channel, expandedAdvFilter, count, filter, onFilterChange, onGetQuickFilterOptions, onSelectedExpandedAdvFilter, onError } = props;

    const api = useCoreApi();

    const { t } = useTranslation("knowledge");

    const [stickers, setStickers] = useState<IAdvFilterCommand[]>([]);
    const [dialog, setDialog] = useState<any>();
    const [dropdownOptions, setDropdownOptions] = useState<IAdvFilterDropdown[]>(getDropdownOptions());

    const orderOptions = useRef<IAdvFilterMenuItem<KnowledgeFeedOrder>[]>(orderMenuOptions(t));

    const isFiltered = filter.advFilters.length > 0 || (filter.searchText != undefined && filter.searchText.length > 0);

    const isEmptyDataset = count == 0 && !isFiltered;

    ///

    function getDropdownOptions() {
        return [
            sourceChannelMenuOptions(t),
            containsMenuOptions(t),
            periodMenuOptions(t),
            stickersMenuOptions(t, stickers),
            othersChannelMenuOptions(t, channel.registered)
        ];
    }

    ///
    const handleNewPublication = (publicationType: PublicationType) => {
        setDialog(
            publicationFactory(
                null,
                publicationType,
                () => setDialog(null),
                {
                    channelId: channel.channelId
                }
            )
        );
    };

    /// Ao carregar o menu de rótulos
    const getStickers = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            api.v1.selectors.stickerAdvFilter({ applyTo: StickerAppliesTo.Publication })
                .then(result => {
                    const filterCommands: IAdvFilterCommand[] = result.data?.map(sticker => {
                        const cmd: IAdvFilterCommand = {
                            command: CoreFilterCommand.KnowledgeSticker,
                            displayName: sticker.name,
                            canRepeat: true,
                            group: 1,
                            fixedValue: sticker.stickerId,
                            quickFilterType: KnowledgeQuickFilterType.CustomSticker,
                            canCheck: true
                        };
                        return cmd;
                    });
                    setStickers(filterCommands);
                })
                .catch(error => {
                    if (onError) onError(error);
                })
                .finally(() => resolve(true));
        });
    };

    ////////////////////////////////////////////////////////////////////////////
    /// Effects
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        setDropdownOptions(getDropdownOptions());
    }, [stickers]);


    ////////////////////////////////////////////////////////////////////////////
    /// Render
    ////////////////////////////////////////////////////////////////////////////

    const endActions = (
        <div>
            {!isEmptyDataset &&
                <>
                    <MprcText color="muted">
                        <MprcFilterIconButton
                            filtered={expandedAdvFilter || filter.advFilters.length > 0}
                            onClick={() => onSelectedExpandedAdvFilter(!expandedAdvFilter)}
                        />
                    </MprcText>

                    <MprcIconButtonDropdown
                        menuItemCheck={true}
                        icon={<IconSort />}
                        title={t("shared:Filter.OrderBy")}
                        selected={filter.order}
                        items={orderOptions.current}
                        onChange={(order) => onFilterChange({ ...filter, order })}
                    />
                </>
            }
        </div>
    );

    return (isEmptyDataset && !channel.allowCreatePublication)
        ? <MprcFilterEmptyDataset message={t("Feed.Channel.EmptyDatasetMessage")} />
        : <Box>

            {dialog}

            {!isEmptyDataset &&
                <QuickFilter
                    feedType={FeedType.Channel}
                    selectedCommands={filter.advFilters}
                    onChange={(selected, quickFilter) => onFilterChange(changeQuickFilter(selected, quickFilter, dropdownOptions, filter))}
                    onGetOptions={onGetQuickFilterOptions}
                    onError={(error => onError(error))}
                />
            }

            <Box marginTop={1}>
                <MprcFilterBox>
                    {
                        expandedAdvFilter
                            ? <AdvancedFilter
                                filter={filter}
                                endComponent={endActions}
                                leftDropdowns={dropdownOptions}
                                onGetStickers={getStickers}
                                quickFilter={{
                                    command: CoreFilterCommand.KnowledgeQuickFilter,
                                    onRemoveQuickFilter: (item) => onFilterChange(clearAdvFilter(item, filter))
                                }}
                                onFilterChange={
                                    (newFilter) => onFilterChange({
                                        ...filter,
                                        ...newFilter,
                                        ftsTitleAuthor: filter.advFilters.find(p => p.command == CoreFilterCommand.KnowledgeContent) == null
                                    })
                                }
                                onError={error => onError(error)}
                            />
                            : <>
                                <MprcFilterLine>
                                    <div>
                                        {
                                            channel.allowCreatePublication
                                                ?
                                                <MprcDropdown
                                                    text={t("Feed.Channel.AddButtonText")}
                                                    title={t("Feed.Channel.AddButtonTitle")}
                                                    startIcon={<IconAdd fixedWidth />}
                                                    items={
                                                        [
                                                            { text: t("Enums.PublicationType.Post", { ns: "core" }), value: PublicationType.Post, group: 1 },
                                                            { text: t("Enums.PublicationType.Question", { ns: "core" }), value: PublicationType.Question, group: 1 },
                                                            { text: t("Enums.PublicationType.Poll", { ns: "core" }), value: PublicationType.Poll, group: 1 },
                                                            { text: t("Enums.PublicationType.List", { ns: "core" }), value: PublicationType.List, group: 1 }
                                                        ]
                                                    }
                                                    onChange={handleNewPublication}
                                                />
                                                :
                                                <Typography variant="h3">{t("Feed.Channel.Filter.TitleView")}</Typography>
                                        }
                                    </div>
                                    {endActions}
                                </MprcFilterLine>

                                <Grid container>
                                    <Grid size="grow">
                                        <MprcSearchTextField
                                            disabled={isEmptyDataset}
                                            initialValue={filter.searchText ?? ""}
                                            placeholder={t("Feed.Channel.SearchTextPlaceholder")}
                                            onConfirm={(value) => onFilterChange({ ...filter, searchText: value, ftsTitleAuthor: value != "" })}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                    }
                </MprcFilterBox>
            </Box>
            {count == 0
                ? <Box mt={3}>
                    <MprcFilterNoRecords
                        message={t(isFiltered ? "Feed.AdvancedFilter.NoRecordsFoundText" : "Feed.Channel.EmptyDatasetMessage")}
                        filtered={isFiltered}
                        onResetFilter={() => onFilterChange(getClearFilter())}
                    />
                </Box>
                : count != undefined &&
                <Stack direction="row" spacing={2} alignItems="center" mt={1}>
                    <MprcFilterCountInfo
                        variant={isFiltered ? "bodyNotice" : count == 0 ? "captionHeader" : "caption"}
                        text={t(isFiltered ? "Feed.Channel.FoundPublicationMessage" : "Feed.Channel.PublicationMessage", { count: count! })}
                        onResetFilter={isFiltered ? () => onFilterChange(getClearFilter()) : undefined}
                    />
                    {!channel.registered && channel.totalPublications > 0 &&
                        <MprcTooltip light arrow placement="right-start" title={t("Publication.Literals.PublicationViewRestrictedText")}>
                            <Box sx={{ ml: 1 }}>
                                <FontAwesomeIcon icon={faCircleInfo}></FontAwesomeIcon>
                            </Box>
                        </MprcTooltip>
                    }
                </Stack>
            }
        </Box>;
};
